import { TZDate } from '@date-fns/tz';
import { addDays } from 'date-fns';
const getTzAdjustedDate = (date, timezone, timeConfig) => {
    const { hours, minutes, seconds, milliseconds } = timeConfig;
    // Get the date formatted for the given timezone
    const tzAdjustedDate = new TZDate(date, timezone);
    // Convert the date to the specified time in the given timezone
    const adjustedYear = tzAdjustedDate.getFullYear();
    const adjustedMonth = tzAdjustedDate.getMonth();
    const adjustedDay = tzAdjustedDate.getDate();
    const tzDate = new TZDate(adjustedYear, adjustedMonth, adjustedDay, hours, minutes, seconds, milliseconds, timezone);
    // Return as a basic Date object
    return new Date(tzDate);
};
export const getTzAdjustedStartOfDay = (date, timezone) => {
    return getTzAdjustedDate(date, timezone, {
        hours: 0,
        minutes: 0,
        seconds: 0,
        milliseconds: 0,
    });
};
export const getTzAdjustedEndOfDay = (date, timezone) => {
    return getTzAdjustedDate(date, timezone, {
        hours: 23,
        minutes: 59,
        seconds: 59,
        milliseconds: 999,
    });
};
/**
 * Adds calendar days to a date as they are counted in the given timezone.
 *
 * Plain `addDays` shifts by 24h-per-day relative to the runtime's local timezone, so adding days
 * across a DST transition in the government's timezone drifts the wall-clock time by an hour. Any
 * "N days from today" threshold that is later compared against a `getTzAdjusted*OfDay` value must
 * use this instead, otherwise the comparison is off by that hour on either side of a transition.
 *
 * @param date - The starting date
 * @param days - Number of calendar days to add (may be negative)
 * @param timezone - The timezone whose calendar days are counted
 * @returns A Date at the same wall-clock time in the timezone, `days` calendar days later
 */
export const addTzAdjustedDays = (date, days, timezone) => {
    return new Date(addDays(new TZDate(date, timezone), days));
};
export const getTzAdjustedMiddleOfDay = (date, timezone) => {
    return getTzAdjustedDate(date, timezone, {
        hours: 12,
        minutes: 0,
        seconds: 0,
        milliseconds: 0,
    });
};
/**
 * Converts a date from the specified timezone to a local Date object that represents
 * the same wall-clock time in the user's browser timezone.
 *
 * This is used when loading data from the database into forms. The stored date represents
 * a time in the government's timezone, but we want to display that same wall-clock time
 * in the form, regardless of the user's actual timezone.
 *
 * @param dateString - ISO string of the date stored in the database (represents time in government's timezone)
 * @param timezone - The timezone the date should be interpreted in (e.g., 'America/New_York')
 * @returns A Date object that displays the same wall-clock time in the browser
 *
 * @example
 * // If stored date represents 7:00 PM ET
 * // and you're viewing in PT browser, this returns a Date that shows as 7:00 PM in PT
 * // (not the converted 4:00 PM PT)
 * convertFromTimezoneForDisplay('2024-01-15T19:00:00-05:00', 'America/New_York')
 */
export const convertFromTimezoneToLocal = (dateString, timezone) => {
    const storedDate = new Date(dateString);
    const tzDate = new TZDate(storedDate, timezone);
    // Extract the wall-clock time components from the specified timezone
    const year = tzDate.getFullYear();
    const month = tzDate.getMonth();
    const day = tzDate.getDate();
    const hours = tzDate.getHours();
    const minutes = tzDate.getMinutes();
    const seconds = tzDate.getSeconds();
    const milliseconds = tzDate.getMilliseconds();
    // Create a local Date with those same components
    // This will display as the same wall-clock time in the browser
    return new Date(year, month, day, hours, minutes, seconds, milliseconds);
};
/**
 * Converts local form date/time values to a Date that represents the same wall-clock
 * time in the specified timezone.
 *
 * This is used when submitting forms. The user enters a date and time in their browser
 * (e.g., 7:00 PM while in PT), but we want to store it as 7:00 PM in the government's
 * timezone (e.g., ET), not as the converted equivalent.
 *
 * @param localDate - The date from the form (in browser's local timezone)
 * @param localTime - The time from the form (in browser's local timezone)
 * @param timezone - The timezone to store the date in (e.g., 'America/New_York')
 * @returns A Date object that when stored represents the correct wall-clock time in the specified timezone
 *
 * @example
 * // If user in PT enters 7:00 PM for a government in ET
 * // This returns a Date that represents 7:00 PM ET (which would be 10:00 PM PT in UTC terms)
 * convertToTimezoneForStorage(dateFromForm, timeFromForm, 'America/New_York')
 */
export const convertToTimezoneForStorage = (localDate, localTime, timezone) => {
    // Extract the wall-clock time components from the local date/time inputs
    const year = localDate.getFullYear();
    const month = localDate.getMonth();
    const day = localDate.getDate();
    const hours = localTime.getHours();
    const minutes = localTime.getMinutes();
    // Create a TZDate with these components in the specified timezone
    // This ensures that the entered time becomes that time in the government's timezone
    const tzDate = new TZDate(year, month, day, hours, minutes, 0, 0, timezone);
    // Return as a standard Date object
    return new Date(tzDate);
};
